import { motion } from 'framer-motion'
import { Calendar, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ScheduledMeeting } from '../types'
import { adjustBrightness } from '../utils/color'

interface CalendlyPanelProps {
  isMobile: boolean
  showCalendlyEmbed: boolean
  showCalendlyOverlay: boolean
  scheduledMeeting: ScheduledMeeting | null
  primaryColor: string
  panelWidth: number
  onClose: () => void
}

const CALENDLY_DESKTOP_WIDTH = 480
// Minimum gap we want between the Calendly panel and the voice panel before
// we fall back to the centered overlay layout (covers tablets / narrow desktops).
const CALENDLY_SIDE_BY_SIDE_MIN_GAP = 24

const buildEmbedSrc = (bookingUrl: string): string => {
  const url = new URL(bookingUrl)
  url.searchParams.set('embed_domain', window.location.hostname)
  url.searchParams.set('embed_type', 'Inline')
  return url.toString()
}

// Tracks viewport width so the desktop variant can fall back to a centered
// overlay when there isn't enough horizontal room next to the voice panel.
const useViewportWidth = (): number => {
  const [width, setWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  )
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

/**
 * Wave 7c: Calendly scheduling iframe — desktop floating panel
 * (left of the voice bot) and mobile centered overlay variants.
 *
 * Pure presentational. Renders nothing when the relevant visibility flag
 * is false or the meeting/bookingUrl is missing.
 */
export const CalendlyPanel = (props: CalendlyPanelProps) => {
  const {
    isMobile,
    showCalendlyEmbed,
    showCalendlyOverlay,
    scheduledMeeting,
    primaryColor,
    panelWidth,
    onClose,
  } = props

  const bookingUrl = scheduledMeeting?.bookingUrl
  const viewportWidth = useViewportWidth()
  // Side-by-side layout fits when the voice panel + Calendly panel + gap fit.
  const fitsSideBySide =
    viewportWidth >= panelWidth + CALENDLY_DESKTOP_WIDTH + CALENDLY_SIDE_BY_SIDE_MIN_GAP

  if (!isMobile && showCalendlyEmbed && bookingUrl && fitsSideBySide) {
    return (
      <motion.div
        key="calendly-panel"
        initial={{ opacity: 0, x: panelWidth }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: panelWidth }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          top: 0,
          right: panelWidth,
          height: '100dvh',
          width: CALENDLY_DESKTOP_WIDTH,
          maxWidth: '100vw',
          backgroundColor: 'white',
          boxShadow: '-4px 0 30px rgba(0, 0, 0, 0.15)',
          // Sit ABOVE the voice panel (zIndex 10000) so the popup is never
          // clipped/hidden when the two stacking contexts overlap.
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Calendly Header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'white',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Calendar size={24} />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>אישור פגישה</h2>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.9 }}>
                {scheduledMeeting?.customerName} • {scheduledMeeting?.formattedTime.split(' בשעה')[0]}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: 8,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Calendly Iframe */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', backgroundColor: '#fff7ed', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#92400e' }}>
              👈 בחר/י שעה מהרשימה ואשר/י את הפגישה
            </div>
          </div>
          <iframe
            src={buildEmbedSrc(bookingUrl)}
            style={{
              flex: 1,
              width: '100%',
              border: 'none',
            }}
            title="Calendly Scheduling"
          />
        </div>
      </motion.div>
    )
  }

  // Fallback: centered overlay used for mobile (isMobile) AND for narrow
  // desktops/tablets where the voice panel + Calendly panel can't sit
  // side-by-side without overlapping.
  const showOverlay =
    (isMobile && showCalendlyOverlay) ||
    (!isMobile && showCalendlyEmbed && !fitsSideBySide)

  if (showOverlay && bookingUrl) {
    return (
      <motion.div
        key="calendly-overlay"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          top: '5%',
          left: '5%',
          width: '90%',
          height: '90%',
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Calendly Header with glowing instruction text */}
        <div
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <motion.div
            animate={{
              textShadow: [
                '0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.6)',
                '0 0 20px rgba(255, 255, 255, 1), 0 0 30px rgba(255, 255, 255, 0.8)',
                '0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.6)',
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{
              fontSize: 16,
              fontWeight: 700,
              textAlign: 'center',
              color: 'white',
            }}
          >
            בחר שעה וגלול למטה לאישור
          </motion.div>
        </div>

        {/* Calendly Iframe */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <iframe
            src={buildEmbedSrc(bookingUrl)}
            style={{
              flex: 1,
              width: '100%',
              border: 'none',
            }}
            title="Calendly Scheduling"
          />
        </div>
      </motion.div>
    )
  }

  return null
}
